import {
  CheckSquare, Sparkles, TrendingUp, Bug, Wrench, Receipt, Tags, FileText,
} from 'lucide-react';

// ─── Credential Rules ───────────────────────────────────────────────────────
// Mirrors MIN_PASSWORD_LENGTH in backend/models.py, which is the authority -
// change both together. Every form that accepts a password reads this, and the
// copy interpolates it as {{min}} so the hint can never drift from the check.
export const MIN_PASSWORD_LENGTH = 8;

// ─── Google app integrations ────────────────────────────────────────────────
// The Email page (Gmail) and Google Calendar sync in the app are off until Google
// verifies their scopes. Bots still reach Gmail and Calendar through their Google
// MCP card, which this does not touch. Mirrors GOOGLE_APP_INTEGRATIONS in
// backend/models.py - change both together.
export const GOOGLE_APP_INTEGRATIONS = false;

// ─── Priority Configuration ─────────────────────────────────────────────────
// Unified priority config used across all views (table, kanban, detail, modal, etc.)
// label/altLabel are English; show `${shortLabel} ${t(labelKey)}` and t(altLabelKey).
export const PRIORITIES = {
  p1: {
    key: 'p1',
    label: 'P1 Critical',
    shortLabel: 'P1',
    altLabel: 'Urgent',
    labelKey: 'tasks.priority_p1',
    altLabelKey: 'bulk_action_bar.priority_urgent',
    textColor: 'text-red-400',
    bgColor: 'bg-red-500',
    bgLight: 'bg-red-500/20',
    borderColor: 'border-red-500',
    combined: 'bg-red-500/20 text-red-400',
    flag: '🔴',
  },
  p2: {
    key: 'p2',
    label: 'P2 High',
    shortLabel: 'P2',
    altLabel: 'High',
    labelKey: 'tasks.priority_p2',
    altLabelKey: 'bulk_action_bar.priority_high',
    textColor: 'text-orange-400',
    bgColor: 'bg-orange-500',
    bgLight: 'bg-orange-500/20',
    borderColor: 'border-orange-500',
    combined: 'bg-orange-500/20 text-orange-400',
    flag: '🟠',
  },
  p3: {
    key: 'p3',
    label: 'P3 Medium',
    shortLabel: 'P3',
    altLabel: 'Medium',
    labelKey: 'tasks.priority_p3',
    altLabelKey: 'bulk_action_bar.priority_medium',
    textColor: 'text-blue-400',
    bgColor: 'bg-blue-500',
    bgLight: 'bg-blue-500/20',
    borderColor: 'border-blue-500',
    combined: 'bg-blue-500/20 text-blue-400',
    flag: '🔵',
  },
  p4: {
    key: 'p4',
    label: 'P4 Low',
    shortLabel: 'P4',
    altLabel: 'Low',
    labelKey: 'tasks.priority_p4',
    altLabelKey: 'bulk_action_bar.priority_low',
    textColor: 'text-gray-400',
    bgColor: 'bg-gray-500',
    bgLight: 'bg-gray-500/20',
    borderColor: 'border-gray-500',
    combined: 'bg-gray-500/20 text-gray-400',
    flag: '⚪',
  },
};

export const PRIORITY_ORDER = { p1: 1, p2: 2, p3: 3, p4: 4 };

export const PRIORITY_LIST = Object.values(PRIORITIES);

// ─── Status Configuration (legacy fallback - prefer custom statusConfigs) ───
// `label` is English; show statusValueName(key, t) from lib/statusName.js.
export const DEFAULT_STATUSES = {
  todo: { label: 'To Do', textColor: 'text-gray-400', bgColor: 'bg-gray-500', bgLight: 'bg-gray-500/20', combined: 'bg-gray-500/20 text-gray-400' },
  in_progress: { label: 'In Progress', textColor: 'text-blue-400', bgColor: 'bg-blue-500', bgLight: 'bg-blue-500/20', combined: 'bg-blue-500/20 text-blue-400' },
  completed: { label: 'Completed', textColor: 'text-green-400', bgColor: 'bg-green-500', bgLight: 'bg-green-500/20', combined: 'bg-green-500/20 text-green-400' },
};

// ─── Task Type Icon Mapping ─────────────────────────────────────────────────
export const TASK_TYPE_ICONS = {
  CheckSquare,
  Sparkles,
  TrendingUp,
  Bug,
  Wrench,
  Receipt,
  Tags,
  FileText,
};

// ─── Label Colors ───────────────────────────────────────────────────────────
export const LABEL_COLORS = [
  '#EF4444', '#F97316', '#F59E0B', '#22C55E',
  '#14B8A6', '#3B82F6', '#8B5CF6', '#EC4899',
  '#6B7280', '#FFFFFF',
];

// ─── Quick Date Helpers ─────────────────────────────────────────────────────
// `label` is English; show t(labelKey) to the user.
export const getQuickDates = () => {
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const laterThisWeek = new Date(today);
  laterThisWeek.setDate(laterThisWeek.getDate() + (4 - today.getDay() + 7) % 7 || 7);
  const thisWeekend = new Date(today);
  thisWeekend.setDate(thisWeekend.getDate() + (6 - today.getDay() + 7) % 7 || 7);
  const nextWeek = new Date(today);
  nextWeek.setDate(nextWeek.getDate() + (8 - today.getDay()) % 7 || 7);

  return {
    today: { date: today, label: 'Today', labelKey: 'common.today' },
    tomorrow: { date: tomorrow, label: 'Tomorrow', labelKey: 'common.tomorrow' },
    laterThisWeek: { date: laterThisWeek, label: 'Later this week', labelKey: 'ui_task.date_picker.later_this_week' },
    thisWeekend: { date: thisWeekend, label: 'This weekend', labelKey: 'ui_task.date_picker.this_weekend' },
    nextWeek: { date: nextWeek, label: 'Next week', labelKey: 'ui_task.date_picker.next_week' },
  };
};

// ─── View Types ─────────────────────────────────────────────────────────────
// `label` is English; show t(labelKey) to the user.
export const VIEW_TYPES = {
  list: { id: 'list', label: 'List', labelKey: 'tasks.views.list' },
  table: { id: 'table', label: 'Table', labelKey: 'tasks.views.table' },
  board: { id: 'board', label: 'Board', labelKey: 'tasks.views.board' },
  calendar: { id: 'calendar', label: 'Calendar', labelKey: 'tasks.views.calendar' },
  timeline: { id: 'timeline', label: 'Timeline', labelKey: 'tasks.views.timeline' },
  gallery: { id: 'gallery', label: 'Gallery', labelKey: 'tasks.views.gallery' },
};

// ─── Utility Functions ──────────────────────────────────────────────────────

/** Extract plain text from a Tiptap JSON description */
export const extractPlainText = (description) => {
  if (!description) return null;
  if (typeof description === 'string') return description;
  if (typeof description !== 'object' || !description.content) return null;
  const walk = (nodes) => nodes.map(node => {
    if (node.type === 'text') return node.text || '';
    if (node.type === 'hardBreak') return ' ';
    if (node.content) return walk(node.content);
    return '';
  }).join('');
  return walk(description.content) || null;
};

/** Extract multi-line plain text from a Tiptap JSON document (or plain string).
 *  Block nodes (paragraphs, headings, ...) become separate lines and hard
 *  breaks become newlines - render the result with `whitespace-pre-wrap`.
 *  Unlike extractPlainText (single-line previews), this preserves line
 *  structure for full-content display such as comments. */
export const extractMultilineText = (content) => {
  if (!content) return '';
  if (typeof content === 'string') return content;
  if (typeof content !== 'object' || !content.content) return '';
  const LINE_BREAK_AFTER = new Set(['paragraph', 'heading', 'codeBlock', 'blockquote']);
  const walk = (node) => {
    if (node.type === 'text') return node.text || '';
    if (node.type === 'hardBreak') return '\n';
    const inner = (node.content || []).map(walk).join('');
    return LINE_BREAK_AFTER.has(node.type) ? `${inner}\n` : inner;
  };
  return (content.content || [])
    .map(walk)
    .join('')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/\n+$/, '');
};

/** Plain text as editor content (Tiptap JSON), shown exactly as written: a blank
 *  line starts a new paragraph and a single line break stays a line break.
 *  The same rule as plain_to_tiptap in backend/utils/rich_text.py. */
export const textToDoc = (text) => {
  const blocks = String(text ?? '')
    .replace(/\r\n/g, '\n')
    .replace(/^\n+|\n+$/g, '')
    .split(/\n[ \t]*\n+/);
  return {
    type: 'doc',
    content: blocks.map((block) => {
      const nodes = [];
      block.split('\n').forEach((line, i) => {
        if (i) nodes.push({ type: 'hardBreak' });
        if (line) nodes.push({ type: 'text', text: line });
      });
      return nodes.length ? { type: 'paragraph', content: nodes } : { type: 'paragraph' };
    }),
  };
};

/** A task description for the editor: editor content as it is; a description
 *  still saved as text keeps its lines (the backend converts those at startup,
 *  utils/task_description.py, but an old cached task may still carry one). */
export const descriptionToDoc = (description) => {
  if (description && typeof description === 'object') return description;
  if (typeof description === 'string' && description.trim()) return textToDoc(description);
  return null;
};

/** Format duration in minutes to human-readable string (pass `t` for the app's language) */
export const formatDuration = (minutes, t) => {
  if (!minutes) return '';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (t) {
    if (h && m) return t('ui_task.duration.hours_minutes', { h, m });
    if (h) return t('ui_task.duration.hours', { h });
    return t('ui_task.duration.minutes', { m });
  }
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
};
